import { call, put, SagaIterator, select } from './sagaEffects';
import { AdditionalHeader, BaseRequest } from 'acscp/api/requests';
import { ApiResponse, ResponseType } from 'acscp/api/responses';
import { AddNotification, ForceUpdate, RecommendUpdate, RemoveLoginToken, RemoveSharingTokens } from 'acscp/actions';
import { Authentication } from 'acscp/api/requests/core/baseRequest';
import history from 'acscp/store/history';
import { AuthenticationDetails, getRequiredAuthenticationDetails } from './getRequiredAuthenticationDetails';
import { InsufficientAuth } from 'acscp/api/responses/core/response';
import log from 'loglevel';
import { currentLocale, hasAuthenticationSelector } from 'acscp/sagas/core/selectors';
import { defineMessages } from 'react-intl';
import { NotificationType } from 'acscp/store/notificationState';
import { home, loginAndRedirectTo } from 'acscp/components/routeHelper';

const messages = defineMessages({
    insufficientAuthentication: {
        id: 'authentication.errors.insufficientAuthentication',
        defaultMessage: 'You do not have access to that resource.',
    },
    invalidSharingLink: {
        id: 'authentication.errors.invalidSharingLink',
        defaultMessage: 'Some of your sharing links were invalid.',
    },
    forbidden: {
        id: 'authentication.errors.forbidden',
        defaultMessage: 'You do not have access to that resource.',
    },
});

/**
 * Perform the specified request, adding sufficient authentication information and handling common error responses.
 * @param apiRequest {BaseRequest} - The request to perform.
 * @param canRetryOnServiceUnavailable {boolean} - Helper flag determining if the function could call itself
 * when server responds with 503. Should not be set by users of the saga.
 */
export function* performRequest<T>(
    apiRequest: BaseRequest<T>,
    canRetryOnServiceUnavailable: boolean = true
): SagaIterator {

    const auth = (yield call(getRequiredAuthenticationDetails, apiRequest)) as unknown as AuthenticationDetails | null;

    if (auth === null) {
        const hasAuthentication = (yield select(hasAuthenticationSelector)) as unknown as boolean;

        if (hasAuthentication) {
            yield put(new AddNotification(
                'insufficientAuthentication', messages.insufficientAuthentication, NotificationType.Error));

            history.push(home());
        } else {
            history.push(loginAndRedirectTo(history.location));
        }

        return new InsufficientAuth();
    }

    const additionalHeaders: AdditionalHeader[] = [];

    // Add all available authentication information
    if (auth.token) {
        additionalHeaders.push({
            key: 'authorization',
            value: `Bearer ${auth.token}`
        });
    }
    if (auth.sharingTokens.length > 0) {
        additionalHeaders.push({
            key: 'X-Sharing-Tokens',
            value: auth.sharingTokens.join(',')
        });
    }

    // Add the current locale to all requests allowing the server to provide translations when appropriate.
    const locale = (yield select(currentLocale)) as unknown as string;
    additionalHeaders.push({
        key: 'X-Preferred-Language',
        value: locale
    });

    const response = (yield call(apiRequest.execute, additionalHeaders)) as unknown as ApiResponse<T>;

    if (response.type !== ResponseType.Fatal &&
        response.type !== ResponseType.ServiceUnavailable &&
        response.type !== ResponseType.InsufficientAuth) {

        const recommendation = response.headers.get('X-Recommended-App-Version');
        if (recommendation) {
            yield put(new RecommendUpdate(recommendation));
        }

        if (response.headers.has('X-App-Update-Required')) {
            yield put(new ForceUpdate());
        }
    }

    switch (response.type) {
        case ResponseType.Unauthorized:
            /*
            Invalid sharing tokens (i.e. not matching to any token in DB) will mark _ANY_ request as Unauthorized
            regardless of any other authentication information.
            Remove these tokens, show an error message, and try again.
            */
            if (response.details.invalidSharingTokens && response.details.invalidSharingTokens.length > 0) {
                yield put(new RemoveSharingTokens(response.details.invalidSharingTokens));
                yield put(new AddNotification(
                    'invalidSharingLink', messages.invalidSharingLink, NotificationType.Error));

                return yield call(performRequest, apiRequest);
            }

            /*
            When provided with an invalid JWT, the server will mark _ANY_ request as Unauthorized and
            add an X-Unauthorized-Reason header.
            If it is present, we should remove the JWT and try again.
            */
            if (response.headers.has('X-Unauthorized-Reason')) {
                yield put(new RemoveLoginToken());
                return yield call(performRequest, apiRequest);
            }

            /*
            Requests not requiring any authentication can only fail with Unauthorized status
            if the server code explicitly returns this error.
            We were able to handle some cases in the previous checks.

            In all other cases, each particular request might behave differently, so we shouldn't do anything
            (for example, authentication requests fail with Unauthorized
            when authentication details can't be found for the provided data).
            */
            if (apiRequest.authenticationRequired === Authentication.None) {
                return response;
            }

            /*
            Since we've reached this point in the code, we can be sure that:
            1. we don't have login or refresh tokens
              (otherwise, we'd either be logged in, or would fail with X-Unauthorized-Reason and retry)
            2. the request does not require login token to be executed
              i.e. sharing token authentication or none at all are acceptable
              (otherwise, this whole function would fail at the top)
            3. request requires some form of authentication, which must be the sharing token,
              because of the previous points.
            4. we must had some valid (i.e. existing in the DB), but expired/disabled sharing tokens.
              This is an allowed state of the store, so we don't need to perform any cleanup,
              however we should notify the user about the problem.

            We check the next conditions to confirm that these statements were true,
            and we didn't end up in an unexpected situation.
            */

            /*
            If the user sent a sharing token, and the request failed with Unauthorized, but did NOT return invalid
            sharing tokens, then all sharing tokens they sent must be valid but deactivated by the booking owner.
            Normally the deactivated sharing tokens are retained, here they are removed to avoid the user being
            considered logged in due to deactivated sharing tokens being present.
            */
            if (auth.sharingTokens.length > 0 && apiRequest.authenticationRequired === Authentication.SharingToken) {
                history.push('/deactivated-link');
                yield put(new RemoveSharingTokens(auth.sharingTokens));
                return response;
            }

            log.error('Execution reached unexpected place. Details:', auth, apiRequest, response);
            return response;

        case ResponseType.Forbidden:
            /*
            Authorization failure.

            This can only happen if authentication was successful,
            so all authentication details were valid, and there were no errors during identification process.
            The actual problem here is user trying to access something that they shouldn't be able to view/perform.
            We can't fix this problem in any way, so just redirect to the root and show an error message.
            */

            /*
            If the user sent a sharing token, and the request failed, but did NOT return invalid sharing tokens, then
            they must have sent a sharing token that is valid but deactivated by the booking owner.
            */
            if (auth.sharingTokens.length > 0 && apiRequest.authenticationRequired === Authentication.SharingToken) {
                history.push('/deactivated-link');
                return response;
            }

            yield put(new AddNotification('forbidden', messages.forbidden, NotificationType.Error));
            history.push(home());
            return response;

        case ResponseType.ServiceUnavailable:
            /*
            Server has encountered a temporary error
            (e.g. all servers are offline, or the servers are in a process of failing over to the backup server,
            or the API is taking too long to respond).
            In most cases, the problem can be fixed by retrying,
            but we shouldn't spam-retry if there is an actual problem, so we just do it once.
            Note however, that if a CORS preflight request fails with a non-200 error, then we won't get this far; it
            will be handled as a FatalError.
            */
            if (canRetryOnServiceUnavailable) {
                return yield call(performRequest, apiRequest, false);
            }
            return response;

        default:
            // Success and all other failing cases
            return response;
    }
}
